"use client";

import { TeachingProfile } from "@/components/TeachingProfile";
import { api } from "convex/_generated/api";
import { useMutation, useQuery } from "convex/react";
import Link from "next/link";
import { UserButton } from "@clerk/nextjs";
import { useEffect, useRef, useState } from "react";
import { ANALYTICS_EVENTS, track } from "@/lib/analyticsEvents";
import {
  ArrowRight,
  BookOpen,
  ClipboardCheck,
  Clock,
  CreditCard,
  Infinity,
  LayoutDashboard,
  Mail,
  Mic,
  ShieldCheck,
  Zap,
} from "lucide-react";
import { cn } from "@/lib/utils";

function formatExpiry(ms: number): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
    }).format(new Date(ms));
  } catch {
    return new Date(ms).toLocaleDateString();
  }
}

function QuickLink({
  href,
  icon: Icon,
  label,
  description,
  accent,
}: {
  href: string;
  icon: typeof LayoutDashboard;
  label: string;
  description: string;
  accent?: "primary" | "accent" | "dark";
}) {
  return (
    <Link
      href={href}
      className="group flex items-center gap-3.5 rounded-xl border border-border bg-card p-3.5 transition-all hover:border-primary/30 hover:bg-muted/30 active:scale-[0.98]"
    >
      <div className={cn(
        "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg",
        accent === "accent" ? "bg-accent/15" : accent === "dark" ? "bg-foreground/8" : "bg-primary/12",
      )}>
        <Icon className={cn(
          "h-4 w-4",
          accent === "accent" ? "text-accent-foreground/70" : accent === "dark" ? "text-foreground/60" : "text-primary-foreground/60",
        )} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground">{label}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground/40 transition-transform group-hover:translate-x-0.5" />
    </Link>
  );
}

export default function SettingsPage() {
  const me = useQuery(api.users.getMe);
  const creditInfo = useQuery(api.creditRequests.getMine);
  const emailPreferences = useQuery(api.emailPreferences.getMine);
  const setMarketingConsent = useMutation(api.emailPreferences.setMarketingConsent);
  const [marketingConsentSaving, setMarketingConsentSaving] = useState(false);
  const viewedRef = useRef(false);

  useEffect(() => {
    if (viewedRef.current) {
      return;
    }
    viewedRef.current = true;
    track(ANALYTICS_EVENTS.settingsPageViewed);
  }, []);

  if (me === undefined) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3">
        <div className="h-9 w-9 rounded-full border-2 border-border border-t-primary dash-spin" aria-hidden />
        <p className="text-sm text-muted-foreground">Loading settings…</p>
      </div>
    );
  }

  if (me === null) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3">
        <div className="h-9 w-9 rounded-full border-2 border-border border-t-primary dash-spin" aria-hidden />
        <p className="text-sm text-muted-foreground">Preparing your account…</p>
      </div>
    );
  }

  const billing = creditInfo?.billing;
  const ui = billing?.billingUiState;

  const isActiveSub = ui?.kind === "subscription_active" || ui?.kind === "subscription_expiring";
  const isExpiring = ui?.kind === "subscription_expiring";
  const hasCredits = !isActiveSub && billing && billing.credits > 0;
  const noAccess = !isActiveSub && (!billing || billing.credits === 0);
  const marketingConsent = emailPreferences?.marketingConsent ?? false;
  const marketingConsentDisabled =
    emailPreferences === undefined || marketingConsentSaving;

  async function updateMarketingConsent(consent: boolean) {
    if (marketingConsentSaving) {
      return;
    }
    setMarketingConsentSaving(true);
    try {
      await setMarketingConsent({ consent });
    } catch (error) {
      console.error("Failed to update marketing email consent", error);
    } finally {
      setMarketingConsentSaving(false);
    }
  }

  return (
    <main className="mx-auto flex w-full max-w-xl flex-col gap-6 pb-10">

      {/* ── Header ─────────────────────────────────────────── */}
      <div className="dash-panel-in flex flex-col gap-1.5">
        <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">Account</p>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground sm:text-[1.75rem]">Settings</h1>
        <p className="text-sm text-muted-foreground">Manage your profile and account preferences.</p>
      </div>

      {/* ── Account identity ───────────────────────────────── */}
      <div className="dash-panel-in dash-stagger-1 flex items-center gap-4 rounded-2xl border border-border bg-card p-5 shadow-sm">
        <UserButton
          appearance={{
            elements: {
              avatarBox: "h-12 w-12",
            },
          }}
        />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-foreground">
            {me.teacherName?.trim() || "Your account"}
          </p>
          <p className="mt-0.5 truncate text-xs text-muted-foreground">
            {me.schoolName?.trim() || "Click the avatar to manage your account"}
          </p>
        </div>
        <span className="shrink-0 text-xs text-muted-foreground">Tap avatar →</span>
      </div>

      {/* ── Current plan ───────────────────────────────────── */}
      {billing && (
        <div className="dash-panel-in dash-stagger-2">
          <div className={cn(
            "flex items-center justify-between gap-4 rounded-2xl border p-5",
            isActiveSub && !isExpiring
              ? "border-primary/30 bg-primary/8"
              : isExpiring
                ? "border-accent/40 bg-accent/8"
                : hasCredits
                  ? "border-primary/20 bg-primary/5"
                  : "border-border bg-muted/20",
          )}>
            <div className="flex items-center gap-3">
              <div className={cn(
                "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl",
                isActiveSub ? "bg-primary/20" : hasCredits ? "bg-primary/12" : "bg-muted",
              )}>
                {isActiveSub
                  ? <Infinity className="h-5 w-5 text-primary-foreground/70" />
                  : hasCredits
                    ? <Zap className="h-5 w-5 text-primary-foreground/60" />
                    : <CreditCard className="h-5 w-5 text-muted-foreground" />
                }
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">
                  {isActiveSub && ui
                    ? `${(ui as { planLabel: string }).planLabel} — Active`
                    : hasCredits
                      ? `${billing.credits} lesson credit${billing.credits === 1 ? "" : "s"}`
                      : "No active plan"}
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {isExpiring && ui && "kind" in ui && ui.kind === "subscription_expiring"
                    ? `Renew by ${formatExpiry((ui as { expiryMs: number }).expiryMs)} · ${(ui as { daysRemaining: number }).daysRemaining}d left`
                    : isActiveSub && ui && "expiryMs" in ui
                      ? `Active until ${formatExpiry((ui as { expiryMs: number }).expiryMs)}`
                      : hasCredits
                        ? "Pay-as-you-go · credits never expire"
                        : "Subscribe or buy credits to generate"}
                </p>
              </div>
            </div>
            <Link
              href="/buy"
              className={cn(
                "shrink-0 rounded-xl px-4 py-2 text-xs font-semibold transition-all hover:opacity-90 active:scale-[0.97]",
                noAccess ? "bg-accent text-accent-foreground" : "border border-border bg-card text-foreground hover:bg-muted",
              )}
            >
              {noAccess ? "Subscribe" : "Manage"}
            </Link>
          </div>
        </div>
      )}

      {/* ── Plan features overview ─────────────────────────── */}
      {isActiveSub && (
        <div className="dash-panel-in dash-stagger-2 flex flex-col gap-3 rounded-2xl border border-border bg-card p-5">
          <p className="text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">Your plan includes</p>
          <div className="grid grid-cols-2 gap-2">
            {[
              { icon: LayoutDashboard, label: "Lesson plans", desc: "Unlimited" },
              { icon: ClipboardCheck, label: "Tests & quizzes", desc: "Pro only" },
              { icon: Mic, label: "Audio generation", desc: "Pro only" },
              { icon: BookOpen, label: "Personal templates", desc: "Pro only" },
            ].map(({ icon: Icon, label, desc }) => (
              <div key={label} className="flex items-start gap-2.5 rounded-xl bg-muted/30 p-3">
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-primary/12">
                  <Icon className="h-3 w-3 text-primary-foreground/60" />
                </div>
                <div>
                  <p className="text-xs font-medium text-foreground">{label}</p>
                  <p className="text-[10px] text-muted-foreground">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Teaching profile ───────────────────────────────── */}
      <TeachingProfile
        key={`${me.teacherName ?? ""}\0${me.schoolName ?? ""}`}
        teacherName={me.teacherName}
        schoolName={me.schoolName}
      />

      {/* ── Email preferences ─────────────────────────────── */}
      <div className="dash-panel-in dash-stagger-3 flex items-start gap-3.5 rounded-2xl border border-border bg-card p-5 shadow-sm">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/12">
          <Mail className="h-4 w-4 text-primary-foreground/60" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground">
            Product updates and tips by email
          </p>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">
            Optional product news, teaching tips, and feature announcements.
            Billing and account emails continue regardless of this setting.
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={marketingConsent}
          disabled={marketingConsentDisabled}
          onClick={() => void updateMarketingConsent(!marketingConsent)}
          className={cn(
            "relative mt-0.5 h-7 w-12 shrink-0 rounded-full border transition-all disabled:cursor-not-allowed disabled:opacity-60",
            marketingConsent
              ? "border-primary bg-primary"
              : "border-border bg-muted",
          )}
        >
          <span
            className={cn(
              "absolute top-0.5 h-5 w-5 rounded-full bg-background shadow-sm transition-transform",
              marketingConsent ? "translate-x-5" : "translate-x-0.5",
            )}
          />
        </button>
      </div>

      {/* ── Profile completeness notice ────────────────────── */}
      {(!me.teacherName?.trim() || !me.schoolName?.trim()) && (
        <div className="dash-panel-in rounded-xl border border-accent/35 bg-accent/8 px-4 py-3.5">
          <p className="text-sm font-semibold text-foreground">Profile incomplete</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Fill in your name and school to unlock lesson plan generation.
          </p>
        </div>
      )}

      {/* ── Quick navigation ───────────────────────────────── */}
      <div className="dash-panel-in dash-stagger-3 flex flex-col gap-3">
        <p className="text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">Quick links</p>
        <div className="flex flex-col gap-2">
          <QuickLink href="/dashboard" icon={LayoutDashboard} label="Dashboard" description="Generate your next lesson plan" accent="primary" />
          <QuickLink href="/tests/new" icon={ClipboardCheck} label="Tests" description="Create tests & quizzes" accent="accent" />
          <QuickLink href="/audio/new" icon={Mic} label="Audio" description="Generate classroom MP3 audio" accent="dark" />
          <QuickLink href="/history" icon={Clock} label="My resources" description="Find lessons, tests, and audio" accent="primary" />
          <QuickLink href="/buy" icon={CreditCard} label="Plan & billing" description="Manage your plan, renew, or buy lesson credits" accent="accent" />
        </div>
      </div>

      {/* ── Admin link ─────────────────────────────────────── */}
      <AdminLink />
    </main>
  );
}

function AdminLink() {
  const isAdmin = useQuery(api.admin.isAdmin);
  if (!isAdmin) return null;
  return (
    <div className="dash-panel-in">
      <Link
        href="/admin"
        className="flex items-center gap-3 rounded-xl border border-border bg-card p-3.5 text-sm font-medium text-muted-foreground transition-all hover:border-primary/30 hover:bg-muted/30 hover:text-foreground active:scale-[0.98]"
      >
        <ShieldCheck className="h-4 w-4 text-success" />
        Admin panel
        <ArrowRight className="ml-auto h-4 w-4 text-muted-foreground/40" />
      </Link>
    </div>
  );
}
